import { useState } from 'react';

/**
 * UserAvatar — renders a profile photo with graceful fallback.
 * If the image fails to load, displays initials on a teal/seafoam circle.
 *
 * Accepts EITHER a `user` object OR explicit src/firstName/lastName props.
 *
 * Props:
 *  user       – object with profile_photo_url, first_name, last_name (preferred)
 *  src        – image URL (legacy)
 *  firstName  – used for initials fallback (legacy)
 *  lastName   – used for initials fallback (legacy)
 *  size       – tailwind size class, e.g. "w-10 h-10" (default)
 *  textSize   – tailwind text size for initials, e.g. "text-sm" (default)
 *  className  – extra classes on the outer wrapper
 */
export default function UserAvatar({
  user,
  src,
  firstName = '',
  lastName = '',
  size = 'w-10 h-10',
  textSize = 'text-sm',
  className = '',
}) {
  const [failed, setFailed] = useState(false);

  // Allow either user object or explicit props
  const effectiveSrc = src ?? user?.profile_photo_url;
  const effectiveFirst = firstName || user?.first_name || '';
  const effectiveLast = lastName || user?.last_name || '';

  const initials = `${effectiveFirst?.[0] || ''}${effectiveLast?.[0] || ''}`.toUpperCase() || '?';

  // Resolve relative /api/upload/image/{id} URLs against the backend base URL
  const resolvedSrc = effectiveSrc && !effectiveSrc.startsWith('http') && !effectiveSrc.startsWith('data:')
    ? `${process.env.REACT_APP_BACKEND_URL}${effectiveSrc}`
    : effectiveSrc;

  if (!resolvedSrc || failed) {
    return (
      <div
        className={`${size} rounded-full bg-seafoam/40 flex items-center justify-center flex-shrink-0 ${className}`}
        data-testid="avatar-fallback"
      >
        <span className={`font-semibold text-teal select-none ${textSize}`}>{initials}</span>
      </div>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={`${effectiveFirst} ${effectiveLast}`.trim() || 'User'}
      onError={() => setFailed(true)}
      className={`${size} rounded-full object-cover flex-shrink-0 ${className}`}
      data-testid="avatar-image"
    />
  );
}
